import { useLayoutEffect, useRef } from "react";

/**
 * Подсветка пункта меню, которая едет к пункту под курсором, а не зажигается
 * на нём, — так в макете v6. Одна на всё меню; на пункте опасного действия
 * становится красноватой.
 *
 * Следит за признаком `data-highlighted`, который Radix ставит и мышью, и
 * клавишами, — поэтому едет и за стрелками. Первый показ — сразу на месте:
 * ехать из ниоткуда незачем.
 *
 * Ставится внутрь меню (или прокручиваемого списка); положение считается от
 * ближайшего позиционированного предка — меню делает таким класс
 * `glass-host`.
 */
export function MenuPlate({
  match = "[data-highlighted]",
  className = "menu-plate",
}: {
  /** Чей признак ловить: пункт под курсором в меню, выбранная доля в сегментах, раздел настроек. */
  match?: string;
  className?: string;
} = {}) {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const plate = ref.current!;
    const menu = plate.parentElement!;
    let shown = false;

    const place = () => {
      const on = menu.querySelector<HTMLElement>(match);
      if (!on) {
        plate.style.opacity = "0";
        shown = false;
        return;
      }
      plate.classList.toggle("danger", on.dataset.variant === "destructive");
      if (!shown) plate.style.transition = "none";
      plate.style.top = `${on.offsetTop}px`;
      plate.style.left = `${on.offsetLeft}px`;
      plate.style.width = `${on.offsetWidth}px`;
      plate.style.height = `${on.offsetHeight}px`;
      plate.style.opacity = "1";
      if (!shown) {
        void plate.offsetWidth;
        plate.style.transition = "";
        shown = true;
      }
    };

    place();
    // По классу (`.tab.on`) — следим за классами, по признаку (`[data-…]`) — за ним.
    const attribute = match.startsWith(".") ? "class" : (/^\[([\w-]+)/.exec(match)?.[1] ?? "data-highlighted");
    const mo = new MutationObserver(place);
    mo.observe(menu, { subtree: true, attributes: true, attributeFilter: [attribute] });
    // Размер меняется (подписи, ширина колонки) — подсветка встаёт на место без езды.
    const ro = new ResizeObserver(() => {
      plate.style.transition = "none";
      place();
      void plate.offsetWidth;
      plate.style.transition = "";
    });
    ro.observe(menu);
    return () => {
      mo.disconnect();
      ro.disconnect();
    };
  }, [match]);

  return <span ref={ref} className={className} aria-hidden />;
}
